/**
 * Which global records that this document already spent its one reload.
 *
 * Also written to sessionStorage, because the flag has to outlive the very
 * navigation it triggers — a global would be gone by the time the new document
 * needs to know it is the second attempt.
 *
 * Nothing clears this automatically. A chunk loading proves nothing about the
 * chunk that is failing, and any expiry is longer-lived than the reload cycle
 * it would have to interrupt, so either would hand a broken document its
 * allowance back and let it loop. The recovery screen's own retry is the only
 * reset, and a player asked for it.
 */
export const CHUNK_RECOVERY_FLAG = '__acChunkReloaded';

/**
 * Where the recovery handler is served from. A file rather than text written
 * into the document, so nothing a request carries can reach it.
 */
export const CHUNK_RECOVERY_SRC = '/chunk-recovery.js';

/** Same window the public script gives the reporter before navigating away. */
const RELOAD_DELAY_MS = 150;

/**
 * Same matching as public/chunk-recovery.js: webpack names the error, but only
 * some paths preserve the name, so the message is matched as well.
 */
export const isChunkLoadFailure = (
  error: { name?: string; message?: string } | undefined
): boolean => {
  if (error === undefined) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  return (
    typeof error.message === 'string' &&
    (error.message.includes('Loading chunk') || error.message.includes('Loading CSS chunk'))
  );
};

let reloadRequestedByThisDocument = false;

/**
 * The React-side half of the recovery the public script performs for errors
 * that reach the window. A chunk that fails while a lazy component renders is
 * caught by the route error boundary instead, and boundary-caught errors never
 * fire window handlers — so without this, that player got the crash screen
 * without the one reload that fixes the common cause (a deploy replaced the
 * chunks this document's HTML still points at; the reloaded document points at
 * the new ones).
 *
 * Shares the public script's flag, so a document spends ONE reload total across
 * both mechanisms and a chunk that always fails still cannot loop. Returns
 * whether a reload is on the way; false means the allowance is already spent
 * and the caller should show its fallback.
 */
export const requestChunkRecoveryReload = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Asked twice by the same document (an effect re-run, several boundaries):
  // the first answer stands.
  if (reloadRequestedByThisDocument) {
    return true;
  }

  if (hasSpentChunkRecoveryReload()) {
    return false;
  }

  rememberChunkRecoveryReload();
  reloadRequestedByThisDocument = true;

  setTimeout(() => {
    try {
      window.location.reload();
    } catch {
      // Nothing left to try if the document will not even reload.
    }
  }, RELOAD_DELAY_MS);

  return true;
};

/** Test-only: forget that this document already asked for its reload. */
export const resetChunkRecoveryReloadRequest = (): void => {
  reloadRequestedByThisDocument = false;
};

type FlaggedWindow = Window & { [CHUNK_RECOVERY_FLAG]?: boolean };

// Storage plus in-page flag, exactly like the public script: storage so the
// flag outlives the navigation it triggers, the in-page flag for documents
// where storage throws.
const hasSpentChunkRecoveryReload = (): boolean => {
  try {
    if (window.sessionStorage.getItem(CHUNK_RECOVERY_FLAG) === '1') {
      return true;
    }
  } catch {
    // Storage is blocked here; the in-page flag is all we have.
  }

  return (window as FlaggedWindow)[CHUNK_RECOVERY_FLAG] === true;
};

const rememberChunkRecoveryReload = (): void => {
  try {
    window.sessionStorage.setItem(CHUNK_RECOVERY_FLAG, '1');
  } catch {
    // The reload still happens, it just cannot be remembered past this document.
  }

  (window as FlaggedWindow)[CHUNK_RECOVERY_FLAG] = true;
};
